import React, { useState } from 'react';
import { Activity, X } from 'lucide-react';
import PageShell from '../../components/PageShell';
import ConnectionGrid from './ConnectionGrid';
import LiveEventFeed from './LiveEventFeed';
import StatsPanel from './StatsPanel';
import UptimeTimeline from './UptimeTimeline';
import IngestPipelineStrip from './IngestPipelineStrip';
import {
  useConnections,
  useConnectionStats,
  useEventStream,
  useUptimeTimeline,
  type ObserveConnection,
  type StatsWindow,
  type UptimeWindow,
} from './useApiObserve';

export default function ApiObservePage() {
  const { data, isLoading } = useConnections();
  const connections = data?.connections || [];
  const hasData = Boolean(data?.has_data);

  const [selected, setSelected] = useState<ObserveConnection | null>(null);
  const [statsWindow, setStatsWindow] = useState<StatsWindow>('24h');
  const [uptimeWindow, setUptimeWindow] = useState<UptimeWindow>('7d');
  const [streamPaused, setStreamPaused] = useState(false);

  const statsQuery = useConnectionStats(selected?.connection_id ?? null, statsWindow);
  const uptimeQuery = useUptimeTimeline(selected?.connection_id ?? null, uptimeWindow);
  const { events, status } = useEventStream(!streamPaused);

  return (
    <PageShell
      title="API Observe"
      subtitle="Timescale connection telemetry (api-observe) — not the Data Flow ingest census or System Health 1h agg"
      icon={Activity}
      iconColor="bg-indigo-600"
    >
      {isLoading && (
        <div className="text-sm text-gray-500">Loading connections…</div>
      )}

      <IngestPipelineStrip />

      <ConnectionGrid
        connections={connections}
        hasData={hasData}
        selectedId={selected?.connection_id ?? null}
        onSelect={setSelected}
      />

      <div className="mt-6">
        <LiveEventFeed
          events={events}
          status={status}
          paused={streamPaused}
          onTogglePause={() => setStreamPaused((p) => !p)}
        />
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/30" onClick={() => setSelected(null)} />
          <div className="ml-auto relative w-full max-w-2xl bg-white shadow-2xl flex flex-col max-h-full overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{selected.label}</h2>
                <p className="text-xs text-gray-500 font-mono">{selected.connection_id}</p>
              </div>
              <button type="button" onClick={() => setSelected(null)} className="p-1 rounded hover:bg-gray-100">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-8">
              <section>
                <h3 className="text-sm font-medium text-gray-700 mb-3">Uptime</h3>
                <UptimeTimeline
                  data={uptimeQuery.data}
                  isLoading={uptimeQuery.isLoading}
                  window={uptimeWindow}
                  onWindowChange={setUptimeWindow}
                />
              </section>
              <section>
                <h3 className="text-sm font-medium text-gray-700 mb-3">Statistics</h3>
                <StatsPanel
                  stats={statsQuery.data?.stats}
                  isLoading={statsQuery.isLoading}
                  window={statsWindow}
                  onWindowChange={setStatsWindow}
                />
              </section>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}
